import { useState } from "react";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { TaskGuidelinesDialog } from "./TaskGuidelinesDialog";
import { TaskReportDialog } from "./TaskReportDialog";
import type { ContributionTaskKey } from "./task-registry";
import { useKitCopy } from "./use-kit-copy";

/**
 * "Consignes" and "Signaler" pills of a task page, with their dialogs.
 * TaskHeader renders them; use this directly only outside a TaskHeader.
 *
 * - `task` / `taskLabel`: which rules and report reasons to show.
 * - `itemId`: id of the phrase or audio on screen, saved with a report.
 * - `extraGuidelines`: localised lines appended to the task's rules.
 * - `compact`: icon-only below `sm` (labels stay for screen readers).
 */
export function TaskHelpPills({
  task,
  taskLabel,
  itemId,
  extraGuidelines,
  compact = true,
  className,
}: {
  task: ContributionTaskKey;
  taskLabel: string;
  itemId?: string | null;
  extraGuidelines?: string[];
  compact?: boolean;
  className?: string;
}) {
  const { copy } = useKitCopy();
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const pill =
    "inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-full border border-border/60 bg-surface px-3 text-sm font-medium text-foreground transition-colors hover:border-brand/40 hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-4";
  const label = compact ? "sr-only sm:not-sr-only" : undefined;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <button type="button" className={pill} onClick={() => setGuidelinesOpen(true)}>
        <Icon name="bookOpen" className="size-4" />
        <span className={label}>{copy.guidelines}</span>
      </button>
      <button type="button" className={pill} onClick={() => setReportOpen(true)}>
        <Icon name="flag" className="size-4" />
        <span className={label}>{copy.report}</span>
      </button>
      <TaskGuidelinesDialog task={task} taskLabel={taskLabel} open={guidelinesOpen} onOpenChange={setGuidelinesOpen} extra={extraGuidelines} />
      <TaskReportDialog task={task} taskLabel={taskLabel} itemId={itemId} open={reportOpen} onOpenChange={setReportOpen} />
    </div>
  );
}
